import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';

import { ApiService, BackendType } from './api.service';
import {
  IEligibleGroupMembersResponse,
  IExternalGroup,
  IExternalGroupDetail,
  IExternalGroupMember,
  IExternalGroupResponse,
} from '../models/api_models';

@Injectable({
  providedIn: 'root',
})
export class ExternalGroupService {
  private apiService = inject(ApiService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private translocoService = inject(TranslocoService);

  private pageCache = new Map<number, IExternalGroup[]>();
  private currentPageSize = 0;
  private currentSearchKey = '';

  externalGroups = signal<IExternalGroup[] | undefined>(undefined);
  totalExternalGroups = signal<number>(0);
  loadingExternalGroups = signal<boolean>(false);
  savingExternalGroup = signal<boolean>(false);
  deletingExternalGroup = signal<boolean>(false);
  groupDetail = signal<IExternalGroupDetail | undefined>(undefined);
  loadingGroupDetail = signal<boolean>(false);
  removingGroupMember = signal<boolean>(false);
  searchResults = signal<IExternalGroupMember[]>([]);
  loadingSearchResults = signal<boolean>(false);
  addingGroupMember = signal<boolean>(false);

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401 || error.status === 403) {
      this.router.navigate(['unauthorized']);
    } else if (error.status === 404) {
      this.router.navigate(['404']);
    } else if (error.error?.detail) {
      this.toastr.error(error.error?.detail);
    } else {
      this.toastr.error(this.translocoService.translate('switchboard.errors.genericApiError'));
    }
  }

  getExternalGroups(page = 1, pageSize = 10, search = '') {
    const searchKey = search.trim();

    if (
      this.currentSearchKey !== searchKey ||
      (this.currentPageSize && this.currentPageSize !== pageSize)
    ) {
      this.pageCache.clear();
    }
    this.currentSearchKey = searchKey;
    this.currentPageSize = pageSize;

    const requestKey = `${searchKey}-${page}-${pageSize}`;
    this.loadingExternalGroups.set(true);
    const params: Record<string, string | number | boolean> = {
      // includeAdGroups: true,
      currentPage: page,
      pageSize,
    };
    if (searchKey) {
      params['search'] = searchKey;
    }

    this.apiService
      .get<IExternalGroupResponse>(
        'api/v1/custom-group',
        {
          params,
        },
        BackendType.SB,
      )
      .subscribe({
        next: response => {
          if (`${searchKey}-${page}-${pageSize}` !== requestKey) {
            return;
          }
          this.externalGroups.set(response.data);
          this.totalExternalGroups.set(response.count);
          this.pageCache.set(page, response.data);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingExternalGroups.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.loadingExternalGroups.set(false);
        },
      });
  }

  getGroupDetail(id: number) {
    this.loadingGroupDetail.set(true);
    this.apiService
      .get<IExternalGroupDetail>(`api/v1/custom-group/${id}`, {}, BackendType.SB)
      .subscribe({
        next: detail => {
          this.groupDetail.set(detail);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingGroupDetail.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.loadingGroupDetail.set(false);
        },
      });
  }

  createExternalGroup(name: string, onSuccess?: (group: IExternalGroup) => void) {
    this.savingExternalGroup.set(true);
    this.apiService
      .post<IExternalGroup>(`api/v1/custom-group`, { body: { name } }, BackendType.SB)
      .subscribe({
        next: group => {
          this.savingExternalGroup.set(false);
          this.toastr.success(this.translocoService.translate('contactManagement.groupSaved'));
          this.clearCacheAndReload();
          onSuccess?.(group);
        },
        error: (error: HttpErrorResponse) => {
          this.savingExternalGroup.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.savingExternalGroup.set(false);
        },
      });
  }

  updateExternalGroup(id: number, name: string) {
    this.savingExternalGroup.set(true);
    this.apiService
      .put<IExternalGroup>(`api/v1/custom-group/${id}`, { body: { name } }, BackendType.SB)
      .subscribe({
        next: () => {
          this.savingExternalGroup.set(false);
          this.toastr.success(this.translocoService.translate('contactManagement.groupSaved'));
          this.clearCacheAndReload();
        },
        error: (error: HttpErrorResponse) => {
          this.savingExternalGroup.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.savingExternalGroup.set(false);
        },
      });
  }

  deleteExternalGroup(id: number) {
    this.deletingExternalGroup.set(true);
    this.apiService.delete<unknown>(`api/v1/custom-group/${id}`, {}, BackendType.SB).subscribe({
      next: () => {
        this.deletingExternalGroup.set(false);
        this.toastr.success(this.translocoService.translate('contactManagement.groupDeleted'));
        this.clearCacheAndReload();
      },
      error: (error: HttpErrorResponse) => {
        this.deletingExternalGroup.set(false);
        this.handleError(error);
      },
      complete: () => {
        this.deletingExternalGroup.set(false);
      },
    });
  }

  removeGroupMember(groupId: number, memberType: string, memberId: number) {
    this.removingGroupMember.set(true);
    this.apiService
      .delete<unknown>(
        `api/v1/custom-group/${groupId}/members/${memberType}/${memberId}`,
        {},
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.removingGroupMember.set(false);
          this.toastr.success(
            this.translocoService.translate('contactManagement.groupMemberRemoved'),
          );
          this.getGroupDetail(groupId);
        },
        error: (error: HttpErrorResponse) => {
          this.removingGroupMember.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.removingGroupMember.set(false);
        },
      });
  }

  clearCache() {
    this.pageCache.clear();
  }

  private clearCacheAndReload() {
    this.clearCache();
    this.getExternalGroups(1, this.currentPageSize || 10, this.currentSearchKey);
  }

  searchMembers(groupId: number | undefined, search: string) {
    const searchTerm = search.trim();
    if (!groupId || !searchTerm) {
      this.searchResults.set([]);
      return;
    }

    const requestKey = `${groupId}-${searchTerm}`;
    this.loadingSearchResults.set(true);

    this.apiService
      .get<IEligibleGroupMembersResponse>(
        `api/v1/custom-group/${groupId}/eligible-members`,
        {
          params: { search: searchTerm },
        },
        BackendType.SB,
      )
      .subscribe({
        next: response => {
          if (`${groupId}-${searchTerm}` !== requestKey) {
            return;
          }
          this.searchResults.set(response.data);
        },
        error: (error: HttpErrorResponse) => {
          if (`${groupId}-${searchTerm}` === requestKey) {
            this.searchResults.set([]);
          }
          this.loadingSearchResults.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.loadingSearchResults.set(false);
        },
      });
  }

  addGroupMember(groupId: number, member: IExternalGroupMember, onSuccess?: () => void) {
    if (!member.type || !member.id) {
      return;
    }
    this.addingGroupMember.set(true);
    this.apiService
      .post<unknown>(
        `api/v1/custom-group/${groupId}/members`,
        {
          body: { member_type: member.type, member_id: member.id },
        },
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.addingGroupMember.set(false);
          this.toastr.success(
            this.translocoService.translate('contactManagement.groupMemberAdded'),
          );
          this.getGroupDetail(groupId);
          this.searchResults.set([]);
          onSuccess?.();
        },
        error: (error: HttpErrorResponse) => {
          this.addingGroupMember.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.addingGroupMember.set(false);
        },
      });
  }
}
